import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Input, Button, Alert, useIsMobile } from '../components/ui';
import PhoneInput from '../components/ui/PhoneInput';
import api from '../utils/api';
import LogoLockup from '../assets/HopeForSomeone.png';
import LogoIcon from '../assets/logo-icon.png';

const ROLES = [
  { value: 'donor',        label: 'Donor',        desc: 'Register & donate blood', icon: '🩸' },
  { value: 'recipient',    label: 'Recipient',     desc: 'Post blood needs',         icon: '🏥' },
  { value: 'organization', label: 'Organization',  desc: 'Run camps & manage needs', icon: '🏢' },
];

const PANEL_BENEFITS = [
  'Real-time matching by blood type & location',
  'Organizations are admin-verified before they can post camps',
  'Free, always — no fees for donors or recipients',
];

function BrandPanel() {
  return (
    <div style={{
      width: '44%', flexShrink: 0,
      background: 'linear-gradient(145deg, var(--red-deeper) 0%, var(--red-dark) 45%, var(--red) 100%)',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      padding: '64px 52px', position: 'relative', overflow: 'hidden',
    }}>
      <div style={{ position: 'absolute', top: '-80px', right: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)' }} />
      <div style={{ position: 'absolute', bottom: '-110px', left: '-60px', width: '260px', height: '260px', borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* The lockup's navy/red wordmark disappears on this red panel, so the
            mark goes on a white tile and the name stays as type. */}
        <div style={{ marginBottom: '48px', display: 'flex', alignItems: 'center', gap: '11px' }}>
          <div style={{
            width: '44px', height: '44px', borderRadius: '11px', background: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            boxShadow: '0 4px 14px rgba(0,0,0,0.16)',
          }}>
            <img src={LogoIcon} alt="" style={{ width: '32px', height: '32px', display: 'block' }} />
          </div>
          <div style={{ fontSize: '18px', fontWeight: 800, color: 'white', letterSpacing: '-0.03em' }}>
            Hope<span style={{ opacity: 0.65 }}>ForSomeone</span>
          </div>
        </div>

        <h2 style={{ fontSize: '30px', fontWeight: 900, color: 'white', lineHeight: 1.15, letterSpacing: '-0.04em', marginBottom: '14px' }}>
          Join a global<br />donor<br />community.
        </h2>
        <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, marginBottom: '40px' }}>
          Every registration brings us closer to a world where no one dies waiting for blood.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {PANEL_BENEFITS.map((b, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
              <span style={{
                width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', color: 'white', fontWeight: 700,
              }}>✓</span>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)', lineHeight: 1.5 }}>{b}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const requestedRole = searchParams.get('role');
  const [role, setRole] = useState(ROLES.some(r => r.value === requestedRole) ? requestedRole : 'donor');
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '' });
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const isMobile = useIsMobile();

  const set = k => e => {
    setFieldErrors(f => ({ ...f, [k]: undefined }));
    setForm(f => ({ ...f, [k]: e.target.value }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Enter your full name.';
    if (!form.email.includes('@')) errs.email = 'Enter a valid email address.';
    // PhoneInput hands back a full E.164 string, so this checks the whole
    // thing — the same shape the backend and Twilio require.
    if (!/^\+[1-9]\d{6,14}$/.test(form.phone)) errs.phone = 'Enter a valid phone number.';
    if (form.password.length < 8) errs.password = 'Minimum 8 characters.';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true); setError('');
    try {
      await api.post('/auth/register', { ...form, role });
      await login(form.email, form.password);
      if (role === 'donor') {
        // Donors need a profile before they can respond to requests — route through setup first.
        navigate(`/donor/setup${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`);
      } else {
        navigate(redirect || (role === 'organization' ? '/org/dashboard' : '/'));
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bb-auth-shell">
      {!isMobile && <BrandPanel />}

      {/* Form panel */}
      <div style={{
        flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--white)',
        padding: isMobile ? '20px 18px 32px' : '52px 60px',
        overflowY: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: '420px', animation: 'bb-fadeUp 0.5s ease both' }}>
          {isMobile && (
            /* Was 200px, which pushed the form most of a phone screen down
               and read as a broken hero rather than a mark. */
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '18px' }}>
              <img src={LogoLockup} alt="HopeForSomeone" style={{ height: '84px', width: 'auto', display: 'block' }} />
            </div>
          )}

          <div style={{ marginBottom: '18px', textAlign: isMobile ? 'center' : 'left' }}>
            <h1 style={{ fontSize: isMobile ? '23px' : '26px', fontWeight: 800, letterSpacing: '-0.03em', marginBottom: '6px', color: 'var(--gray-900)' }}>
              Join HopeForSomeone
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--gray-500)' }}>
              {redirect
                ? "Quick registration — you'll be taken straight back to the request to respond."
                : 'Create your free account — takes under 2 minutes.'}
            </p>
          </div>

          {/* Role selector */}
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--gray-700)', marginBottom: '6px' }}>I want to</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '8px' }}>
              {ROLES.map(r => (
                <button
                  key={r.value}
                  type="button"
                  onClick={() => setRole(r.value)}
                  aria-pressed={role === r.value}
                  style={{
                    padding: '11px 8px', borderRadius: '9px', textAlign: 'center',
                    cursor: 'pointer',
                    border: `2px solid ${role === r.value ? 'var(--red)' : 'var(--gray-200)'}`,
                    background: role === r.value ? 'var(--red-50)' : 'var(--white)',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <span style={{ fontSize: '18px', display: 'block', marginBottom: '4px' }}>{r.icon}</span>
                  <span style={{ fontSize: '11.5px', fontWeight: 700, display: 'block', color: role === r.value ? 'var(--red-dark)' : 'var(--gray-800)' }}>{r.label}</span>
                  <span style={{ fontSize: '10px', color: 'var(--gray-400)', lineHeight: 1.3, display: 'block', marginTop: '2px' }}>{r.desc}</span>
                </button>
              ))}
            </div>
          </div>

          <Input label="Full Name"  placeholder="Ravi Kumar" autoFocus value={form.name} onChange={set('name')} error={fieldErrors.name} />
          <Input label="Email"      placeholder="you@email.com"     type="email"           value={form.email}    onChange={set('email')} error={fieldErrors.email} />

          <PhoneInput
            label="Phone"
            value={form.phone}
            onChange={v => {
              setFieldErrors(f => ({ ...f, phone: undefined }));
              setForm(f => ({ ...f, phone: v }));
            }}
            error={fieldErrors.phone}
          />

          <Input label="Password"   placeholder="Min 8 characters"  type="password"         value={form.password} onChange={set('password')} error={fieldErrors.password} />

          {error && <Alert variant="error">{error}</Alert>}

          <Button variant="primary" size="lg" fullWidth loading={loading} onClick={handleSubmit}>
            Create Account
          </Button>

          <p style={{ textAlign: 'center', fontSize: '11.5px', color: 'var(--gray-400)', marginTop: '10px', lineHeight: 1.5 }}>
            By creating an account, you agree to our{' '}
            <Link to="/terms" style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Terms of Service</Link>{' '}
            and{' '}
            <Link to="/privacy" style={{ color: 'var(--gray-500)', fontWeight: 600 }}>Privacy Policy</Link>.
          </p>

          <p style={{ textAlign: 'center', fontSize: '13.5px', color: 'var(--gray-500)', marginTop: '14px' }}>
            Already have an account?{' '}
            <Link
              to={`/login${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ''}`}
              style={{ fontWeight: 600, color: 'var(--red)' }}
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
